import * as React from 'react'
import { Link } from 'react-router-dom'
import { useTable, usePagination, Column, useSortBy } from 'react-table'
import { OrganizationRoles } from '../../constants/organization-team-member-roles'
import {
  OrganizationTeamMemberDisplayStatuses,
  OrganizationTeamMemberStatus
} from '../../constants/organization-team-member-statuses'
import { IOrganizationTeamMember, IMemberFilters, IMemberSearchObject } from '../../types'
import ArchiveIcon from '../icons/ui/Archive'
import RestoreArchiveIcon from '../icons/ui/RestoreArchive'
import SendInviteIcon from '../icons/ui/SendInvite'
import Edit from '../icons/drivers/Edit'
import ImageView from '../ui/ImageView'
import UnlockIcon from '../icons/ui/Unlock'
import ContextMenu from '../ui/ContextMenu'
import ReactTablePagination from '../ui/ReactTablePagination'
import classNames from 'classnames'
import { filterAssistances } from 'src/state/selectors'
import UIMessages from '../../utils/ui-messages'
import toast, { Toaster } from 'react-hot-toast'
import { getAccessJwtPayload } from '../../utils/auth'
import { checkIfAdmin } from 'src/utils/authorization'
export interface ITeamMemberListProps {
  filters: IMemberFilters
  teamMembers: IMemberSearchObject[]
  total: number
  isFetching: boolean
  type: string
  statuses: OrganizationTeamMemberStatus[]
  fetchOrgList: () => void
  fetchTeamMemberList: (filters: IMemberFilters) => Promise<any>
  archive: (teamMember: IMemberSearchObject) => Promise<any>
  resetPassword: (teamMember: IMemberSearchObject) => Promise<any>
  sendInvitation: (teamMember: IMemberSearchObject) => Promise<any>
  updateFilters: (params: IMemberFilters) => Promise<any>
  currentOrgId: string
}

interface ITeamMemberTableState {
  pages: number
  pageSize: number
  isSaving: boolean
}

interface IProps<T extends object> {
  columns: Array<Column<T>>
  data: T[]
  loading: boolean
  pageCount: number
  filters: IMemberFilters
  fetchData: (filters: IMemberFilters, params: { pageIndex: number; pageSize: number }) => void
  updateFilters: (params: IMemberFilters) => Promise<any>
}

const PAGE_SIZE = 10

function Table<T extends object>({
  columns,
  data,
  fetchData,
  updateFilters,
  loading,
  pageCount: controlledPageCount
}: IProps<T>) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    filters,
    state
  } = useTable<T>(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: PAGE_SIZE },
      manualSortBy: true,
      manualPagination: true,
      pageCount: controlledPageCount
    },
    useSortBy,
    usePagination
  )
  const { pageIndex, pageSize, sortBy } = state

  React.useEffect(() => {
    fetchData(filters, { pageIndex, pageSize })
  }, [filters, pageIndex, pageSize])

  React.useEffect(() => {
    const sortParams = sortBy.map(s => {
      return s.id + '-' + (s.desc ? 'desc' : 'asc')
    })
    updateFilters({ sortBy: sortParams })
  }, [sortBy])

  //Declaration of URL's
  const getLink = (): string => {
    let link = ''
    if (process.env.REACT_APP_GATEWAY_URL) {
      link = process.env.REACT_APP_GATEWAY_URL
      if (process.env.REACT_APP_GATEWAY_URL.includes('dev')) {
        const lnk = process.env.REACT_APP_GATEWAY_URL.split(':')
        link = lnk[1]
      } else if (process.env.REACT_APP_GATEWAY_URL.includes('radar')) {
        const lnk1 = process.env.REACT_APP_GATEWAY_URL.split(':')
        link = lnk1[1]
      }
    }
    return link
  }
  //Declaration of URL's
  return (
    <React.Fragment>
      <Toaster position='top-right' reverseOrder={false} />
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  className={classNames('', {
                    sortable: column.canSort,
                    sortedAsc: column.isSortedDesc === false,
                    sortedDesc: column.isSortedDesc === true
                  })}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {loading ? (
            <td>Loading...</td>
          ) : (
            rows.map(
              row =>
                +prepareRow(row) || (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => {
                      return (
                        <td {...cell.getCellProps()} key={`teamMemberColumn-${cell.row.index}-${cell.column.id}`}>
                          {cell.render('Cell')}
                        </td>
                      )
                    })}
                  </tr>
                )
            )
          )}
        </tbody>
      </table>
      <ReactTablePagination
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        canNext={canNextPage}
        canPrevious={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
      />
      {/* Footer */}
      <div
        className='footer-copyright border-box text-left py-3 font-black h6 bold '
        style={{ marginBottom: '5rem', marginTop: '2rem' }}
      >
        &copy; Copyright {new Date().getFullYear()}{' '}
        <a href={`${getLink()}`}>
          <u>GOIN'</u>
        </a>{' '}
        All rights reserved.
      </div>
      {/* Footer */}
    </React.Fragment>
  )
}

const CellBasicInfo = (props: { name: string; id: string }) => (
  <div className='flex items-center'>
    <ImageView entityId={props.id} isRounded={true} />
    <p className='ml1 TeamMemberName font-dark-grey'>{props.name}</p>
  </div>
)

const ActiveTeamMemberMenu = (props: {
  refreshData: () => Promise<any>
  teamMember: IMemberSearchObject
  sendInvitation: (teamMember: IMemberSearchObject) => Promise<any>
  resetPassword: (teamMember: IMemberSearchObject) => Promise<any>
  archive: (teamMember: IMemberSearchObject) => Promise<any>
}) => {
  const menuCtx = React.useRef<any>()
  const { teamMember, sendInvitation, resetPassword, archive, refreshData } = props
  const accessJwtPayload = getAccessJwtPayload()
  const isAdmin = checkIfAdmin()
  const onSendInvitationClick = () => {
    // alert('send invite')
    if (isAdmin) {
      toast.success(UIMessages.teamMember.invitationMessage)
      sendInvitation(teamMember)
    }
  }

  const onResetPasswordClick = () => {
    if (isAdmin) {
      resetPassword(teamMember)
    }
  }
  const onArchiveClickClick = async () => {
    if (isAdmin) {
      await archive(teamMember)
      setTimeout(refreshData, 1000)
      menuCtx.current.mouseLeave()
    }
  }
  return isAdmin ? (
    <ContextMenu ref={menuCtx} style={{ marginRight: '8px', zDepth: -1 }}>
      {isAdmin && (
        <div className='MenuItem cursor--pointer p1'>
          <div className='flex items-center' onClick={onSendInvitationClick}>
            <SendInviteIcon className='icon--small mr1 svg-stroke-light-grey' />
            <div>Send Invite</div>
          </div>
        </div>
      )}
      {isAdmin && (
        <div className='MenuItem cursor--pointer p1'>
          <div className='flex items-center' onClick={onResetPasswordClick}>
            <UnlockIcon className='icon--small mr1 svg-stroke-light-grey' />
            <div>Reset password</div>
          </div>
        </div>
      )}
      {isAdmin && (
        <div className='MenuItem cursor--pointer p1'>
          <Link className='flex items-center' to={`/team-members/edit/${teamMember.id}`}>
            <Edit className='icon--small mr1 svg-stroke-light-grey' />
            <div>Edit</div>
          </Link>
        </div>
      )}
      {isAdmin && (
        <div className='MenuItem cursor--pointer p1'>
          <div className='flex items-center' onClick={onArchiveClickClick}>
            <ArchiveIcon className='icon--small mr1 svg-stroke-light-grey' />
            <div>{teamMember.isArchived ? 'Restore from Archive' : 'Move to Archive'}</div>
          </div>
        </div>
      )}
    </ContextMenu>
  ) : null
}

const ArchivedTeamMemberMenu = (props: {
  refreshData: () => Promise<any>
  teamMember: IOrganizationTeamMember
  archive: (teamMember: IOrganizationTeamMember) => Promise<any>
}) => {
  const { teamMember, archive, refreshData } = props
  const clickArchive = async () => {
    await archive(teamMember)
    await refreshData()
  }
  return (
    <ContextMenu>
      <div onClick={clickArchive} className='MenuItem flex items-center cursor--pointer p1'>
        <div>
          <RestoreArchiveIcon className='icon--small svg-stroke-grey3 mr1' />
          <span className='font-dark-grey'>Restore</span>
        </div>
      </div>
    </ContextMenu>
  )
}

export default function TeamMemberTable(props: ITeamMemberListProps) {
  const [d, setData] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const [pageCount, setPageCount] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(PAGE_SIZE)
  const { type, teamMembers } = props

  const accessJwtPayload = getAccessJwtPayload()
  const isAdmin = checkIfAdmin()

  const columns = React.useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: (data: any) => {
          const { id, userId, name } = data.row.original
          if (isAdmin) {
            return <Link to={`/team-members/${id}`}>{name}</Link>
          } else {
            return <span>{name}</span>
          }
        }
      },
      {
        Header: 'Roles',
        id: 'roles',
        disableSortBy: true,
        accessor: ({ roles }: any) => {
          return roles.map((it: any) => OrganizationRoles[it.roleName]).join(', ')
        },
        className: 'flex items-center'
      },
      {
        Header: 'Phone number',
        accessor: (teamMember: IMemberSearchObject) => {
          let phoneNumber = ''
          if (teamMember.phone.indexOf(teamMember.countryCode) === 0) {
            phoneNumber = teamMember.phone.replace(teamMember.countryCode, '')
          } else {
            phoneNumber = teamMember.phone.replace('+', '')
          }

          return phoneNumber !== '' && phoneNumber !== '1' ? '+' + phoneNumber : 'none'
        },
        disableSortBy: true,
        className: 'flex items-center'
      },
      {
        Header: 'Email',
        accessor: 'email',
        className: 'flex items-center'
      },
      {
        Header: 'Status',
        id: 'status',
        accessor: ({ status, invitationTime }: any) => {
          invitationTime = new Date(invitationTime)

          if (new Date().getTime() > invitationTime.getTime()) {
            status = 'INVITATION_EXPIRED'
          }
          return OrganizationTeamMemberDisplayStatuses[status]
        },
        className: 'flex items-center',
        show: type !== 'archived'
      },
      {
        id: 'archive-menu',
        show: type !== 'archived',
        maxWidth: 40,
        className: 'overflow--visible',
        Cell: (data: any) => {
          const refresh = () => fetchTeamMemberData(props.filters, { pageSize, pageIndex: 0 })
          return (
            <ActiveTeamMemberMenu
              archive={props.archive}
              resetPassword={props.resetPassword}
              sendInvitation={props.sendInvitation}
              teamMember={data.row.original}
              refreshData={refresh}
            />
          )
        }
      }
    ],
    [props.filters, props.teamMembers]
  )

  React.useEffect(() => {
    props.fetchOrgList()
  }, [])

  React.useEffect(() => {
    fetchTeamMemberData(props.filters, { pageSize, pageIndex: 0 })
  }, [props.filters])

  const fetchTeamMemberData = React.useCallback(async (filters: IMemberFilters, { pageSize, pageIndex }) => {
    const total = await props.fetchTeamMemberList({
      ...filters,
      ...props.filters,
      skip: pageIndex * pageSize,
      take: pageSize
    })
    setPageSize(pageSize)
    setPageCount(Math.ceil(total.total / pageSize))
  }, [props.filters])
  return (
    <div className='react-table'>
      <Table
        columns={columns}
        data={props.teamMembers}
        filters={props.filters}
        fetchData={fetchTeamMemberData}
        updateFilters={props.updateFilters}
        loading={loading}
        pageCount={pageCount}
      />
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/components/team-members/TeamMemberTable.tsx